import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from './store';
import axios from 'axios';
import { environment } from '../environments/environment';
import { AnalyticsSearchOptions } from '../pages/Analytics/analytics';
import { AdditionalHours } from '../models/additional-hours';
import { calculateDateRange } from './sessionSlice';

// Models Start
interface Approval {
  id: string;
  hours: number;
  reason: string;
  approved: boolean | null;
  time: string;
  createTime: string;
  approvedTime?: string;
  approvedBy?: string;
  approvedByName?: string;
  denialReason?: string;
  createUser: {
    firstName: string;
    lastName: string;
  };
  location: {
    name: string;
    id: string;
  };
  supplier: {
    name: string;
    id: string;
  };
  attachments: any[];
}

interface ApprovalState {
  pendingApprovals: Approval[];
  approvedApprovals: Approval[];
  deniedApprovals: Approval[];
  allApprovals: Approval[];
  loading: boolean;
  error: string | null;
}

const initialState: ApprovalState = {
  pendingApprovals: [],
  approvedApprovals: [],
  deniedApprovals: [],
  allApprovals: [],
  loading: false,
  error: null,
};

interface ApprovalApiParams {
  before?: string;
  after?: string;
  status?: 'pending' | 'approved' | 'denied';
  id?: string;
}
// Models End

// API Calls Start
const apiFetchApprovalData = async (params: ApprovalApiParams) => {
  const baseURL = environment.apiURL;
  let queryParams = '?fields=all,supplier.all,location.all,location.oem.all,createUser.all,attachments.all,attachments.attachment';
  
  if (params.after) {
    queryParams += `&after=${params.after}`;
  }
  if (params.before) {
    queryParams += `&before=${params.before}`;
  }
  if (params.status) {
    queryParams += `&status=${params.status}`;
  }

  const response = await axios.get(`${baseURL}/additionalHours${queryParams}`);
  return response.data;
};

const apiUpdateApproval = async (approval: any) => {
  const baseURL = environment.apiURL;
  const response = await axios.put(`${baseURL}/additionalHours/${approval.id}`, approval);
  return response.data;
}

const apiGetAdditionalHoursUsingAnalyticsSearchOptions = async (options: AnalyticsSearchOptions, dispatch: any): Promise<AdditionalHours[]> => {
  const baseURL = environment.apiURL;
  var params = '?fields=all,supplier.all,location.all,location.oem.all,createUser.all';

  const rangeResult = await dispatch(calculateDateRange(options.timePeriod));
  if (rangeResult.type.endsWith('/rejected')) {
    throw new Error('Failed to calculate date range');
  }
  const range = rangeResult.payload;

  if (range.after) {
      params += `&after=${options.customStartDate ?? range.after.toISOString()}`;
  }

  if (range.before) {
      params += `&before=${options.customEndDate ?? range.before.toISOString()}`;
  }

  const response = await axios.get(`${baseURL}/additionalHours${params}`);
  return response.data;
}
// API Calls End

// Thunks Start
export const fetchAllApprovals = createAsyncThunk(
  'approvals/fetchAllApprovals',
  async ({ before, after }: { before?: string; after?: string }) => {
    return await apiFetchApprovalData({ before, after });
  }
);

export const fetchPendingApprovals = createAsyncThunk(
  'approvals/fetchPendingApprovals',
  async () => {
    return await apiFetchApprovalData({ status: 'pending' });
  }
);

export const updateApproval = createAsyncThunk(
  'approvals/updateApproval',
  async (approval: Partial<Approval>) => {
    const response = await apiUpdateApproval(approval);
    return response;
  }
);

export const fetchAdditionalHoursUsingAnalyticsSearchOptions = createAsyncThunk(
  'approvals/fetchAdditionalHoursUsingAnalyticsSearchOptions',
  async (options: AnalyticsSearchOptions, { dispatch }) => {
    return await apiGetAdditionalHoursUsingAnalyticsSearchOptions(options, dispatch);
  }
);
// Thunks End

// Slice Start
const approvalSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    clearApprovals: (state) => {
      state.allApprovals = [];
      state.pendingApprovals = [];
      state.approvedApprovals = [];
      state.deniedApprovals = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Approvals
      .addCase(fetchAllApprovals.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllApprovals.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Sort and categorize approvals
        const approvals = action.payload;
        state.allApprovals = approvals;
        
        // Sort by create time in descending order
        approvals.sort((a: Approval, b: Approval) => (a.createTime > b.createTime ? -1 : 1));
        
        // Filter approvals
        state.approvedApprovals = approvals
          .filter((approval: Approval) => approval.approved === true)
          .sort((a: Approval, b: Approval) => ((a.approvedTime ?? '') > (b.approvedTime ?? '') ? -1 : 1));
          
        state.deniedApprovals = approvals
          .filter((approval: Approval) => approval.approved === false)
          .sort((a: Approval, b: Approval) => ((a.approvedTime ?? '') > (b.approvedTime ?? '') ? -1 : 1));
      })
      .addCase(fetchAllApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch approvals';
      })
      
      // Fetch Pending Approvals
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Filter and sort pending approvals
        const pendingApprovals = action.payload;
        pendingApprovals.sort((a: Approval, b: Approval) => (a.createTime > b.createTime ? -1 : 1));
        
        state.pendingApprovals = pendingApprovals.filter(
          (approval: Approval) => approval.approved === null
        );
        
        // Update allApprovals to include pending ones
        const existingIds = new Set(state.allApprovals.map(a => a.id));
        const newPendingApprovals = state.pendingApprovals.filter(
          a => !existingIds.has(a.id)
        );
        state.allApprovals = [...state.allApprovals, ...newPendingApprovals];
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pending approvals';
      })
      
      // Update Approval
      .addCase(updateApproval.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateApproval.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Update the approval in all relevant lists
        const updatedApproval = action.payload;
        const updateApprovalInList = (list: Approval[]) => {
          const index = list.findIndex(a => a.id === updatedApproval.id);
          if (index !== -1) {
            list[index] = updatedApproval;
          }
        };
        
        updateApprovalInList(state.allApprovals);
        updateApprovalInList(state.pendingApprovals);
        updateApprovalInList(state.approvedApprovals);
        updateApprovalInList(state.deniedApprovals);
      })
      .addCase(updateApproval.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update approval';
      });
  },
});
// Slice End

// Selectors Start
export const selectAllApprovals = (state: RootState) => state.approvals.allApprovals;
export const selectPendingApprovals = (state: RootState) => state.approvals.pendingApprovals;
export const selectApprovedApprovals = (state: RootState) => state.approvals.approvedApprovals;
export const selectDeniedApprovals = (state: RootState) => state.approvals.deniedApprovals;
export const selectApprovalLoading = (state: RootState) => state.approvals.loading;
// Selectors End

// Actions Start
export const { clearApprovals } = approvalSlice.actions;
// Actions End

// Reducer Start
export default approvalSlice.reducer; 
// Reducer End